import { useEffect, useState } from "react";
import { Plus, Trash2, Save, X, MapPin, Loader2, QrCode, Printer, Copy } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { usePublicSiteBase } from "@/lib/usePublicSiteBase";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MediaUpload from "@/components/admin/MediaUpload";

const CATS = ["restaurants", "attractions", "hiking", "grocery", "coffee", "icecream", "golf"];
const CAT_LABELS = { restaurants: "Restaurants", attractions: "Attractions", hiking: "Hiking Trails", grocery: "Grocery Stores", coffee: "Coffee Shops", icecream: "Ice Cream Shops", golf: "Local Golf Courses" };
const blank = { category: "restaurants", title: "", description: "", image_url: "", address: "", distance: "", latitude: null, longitude: null, website_url: "", tags: [], order: 0 };

export default function EditRecommendations() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [tagInput, setTagInput] = useState("");
  const [geoLoading, setGeoLoading] = useState(false);

  const load = () => api.get("/recommendations").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const geocode = async () => {
    if (!editing.address?.trim()) { toast.error("Enter an address first"); return; }
    setGeoLoading(true);
    try {
      const { data } = await api.get(`/geocode?q=${encodeURIComponent(editing.address)}`);
      setEditing((prev) => ({ ...prev, latitude: data.latitude, longitude: data.longitude }));
      toast.success("Location pinned from address");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not find that address");
    } finally { setGeoLoading(false); }
  };

  const openNew = () => { setEditing({ ...blank, order: items.length }); setTagInput(""); };
  const save = async () => {
    if (!editing.title) { toast.error("Title required"); return; }
    if (editing.id) await api.put(`/recommendations/${editing.id}`, editing);
    else await api.post("/recommendations", editing);
    toast.success("Saved"); setEditing(null); load();
  };
  const remove = async (id) => { await api.delete(`/recommendations/${id}`); toast.success("Deleted"); load(); };
  const addTag = () => { if (tagInput.trim()) { setEditing({ ...editing, tags: [...editing.tags, tagInput.trim()] }); setTagInput(""); } };

  const origin = usePublicSiteBase();
  const hikingUrl = `${origin}/local-tips?cat=hiking`;
  const printHikingQR = () => {
    const canvas = document.querySelector('[data-testid="hiking-qr-code"] canvas');
    if (!canvas) return;
    const img = canvas.toDataURL("image/png");
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>Hiking Trails QR</title></head>
      <body style="font-family:Arial;text-align:center;padding:40px">
      <h2 style="color:#2C3B2E">Hiking Trails Near Apache Junction</h2>
      <p style="color:#666">Scan to explore local hiking trails, maps & directions</p>
      <img src="${img}" style="width:300px;height:300px"/>
      </body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><span className="overline text-brand-terracotta">Edit</span><h1 className="font-display text-4xl tracking-tight mt-1">Local Spots</h1></div>
        <Button onClick={openNew} data-testid="add-recommendation" className="rounded-full bg-brand-forest hover:bg-brand-terracotta text-white active:scale-95 transition-colors"><Plus size={16} className="mr-2" />Add</Button>
      </div>

      <div className="mb-8 rounded-2xl border-2 border-brand-terracotta/30 bg-brand-terracotta/5 p-5" data-testid="hiking-qr-panel">
        <Label className="mb-2 block flex items-center gap-2"><QrCode size={16} className="text-brand-terracotta" /> Hiking Trails QR <span className="text-brand-forest/40 font-normal">(print on a card — guests scan to open the Hiking Trails page)</span></Label>
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <div className="p-3 bg-white rounded-2xl border border-black/5" data-testid="hiking-qr-code">
            <QRCodeCanvas value={hikingUrl} size={148} fgColor="#2C3B2E" bgColor="#ffffff" level="M" includeMargin />
          </div>
          <div className="flex-1 w-full">
            <Label className="mb-1.5 block text-xs uppercase tracking-wide text-brand-forest/50">Hiking Trails link</Label>
            <div className="flex gap-2">
              <Input readOnly value={hikingUrl} data-testid="hiking-url" className="bg-white text-sm" />
              <Button type="button" variant="outline" onClick={() => { navigator.clipboard.writeText(hikingUrl); toast.success("Link copied"); }} data-testid="hiking-copy" className="rounded-full shrink-0"><Copy size={16} /></Button>
            </div>
            <Button type="button" onClick={printHikingQR} data-testid="hiking-print" className="mt-3 rounded-full bg-brand-forest hover:bg-brand-terracotta text-white"><Printer size={16} className="mr-2" />Print QR</Button>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((it) => (
          <div key={it.id} data-testid={`admin-rec-${it.id}`} className="bg-white rounded-2xl p-5 border border-black/5 shadow-sm flex justify-between gap-3">
            <div>
              <span className="overline text-brand-terracotta">{it.category}</span>
              <h3 className="font-display text-lg tracking-tight">{it.title}</h3>
              <p className="text-sm text-brand-forest/60 line-clamp-2 mt-1">{it.description}</p>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <button onClick={() => { setEditing(it); setTagInput(""); }} data-testid={`edit-rec-${it.id}`} className="text-sm font-medium text-brand-terracotta hover:underline">Edit</button>
              <button onClick={() => remove(it.id)} data-testid={`delete-rec-${it.id}`} className="text-brand-forest/30 hover:text-destructive transition-colors"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="rec-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing?.id ? "Edit" : "New"} Recommendation</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4 pt-2">
              <div><Label className="mb-1.5 block">Category</Label>
                <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                  <SelectTrigger data-testid="rec-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{CAT_LABELS[c]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1.5 block">Title</Label><Input data-testid="rec-title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></div>
              <div><Label className="mb-1.5 block">Description</Label><Textarea data-testid="rec-desc" rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
              <div><Label className="mb-1.5 block">Website link <span className="text-brand-forest/40">(optional — shows a clickable link on the card)</span></Label><Input data-testid="rec-website" type="url" value={editing.website_url || ""} onChange={(e) => setEditing({ ...editing, website_url: e.target.value })} placeholder="https://example.com" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="mb-1.5 block">Address</Label><Input data-testid="rec-address" value={editing.address} onChange={(e) => setEditing({ ...editing, address: e.target.value })} /></div>
                <div><Label className="mb-1.5 block">Distance</Label><Input data-testid="rec-distance" value={editing.distance} onChange={(e) => setEditing({ ...editing, distance: e.target.value })} /></div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="block">Map Coordinates <span className="text-brand-forest/40">(shows a pin on the guest map)</span></Label>
                  <button type="button" onClick={geocode} disabled={geoLoading} data-testid="rec-geocode"
                    className="flex items-center gap-1.5 text-sm font-semibold text-brand-terracotta hover:underline disabled:opacity-50">
                    {geoLoading ? <Loader2 size={15} className="animate-spin" /> : <MapPin size={15} />} Find from address
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input data-testid="rec-lat" type="number" step="any" placeholder="Latitude e.g. 33.4942" value={editing.latitude ?? ""} onChange={(e) => setEditing({ ...editing, latitude: e.target.value === "" ? null : parseFloat(e.target.value) })} />
                  <Input data-testid="rec-lng" type="number" step="any" placeholder="Longitude e.g. -111.9261" value={editing.longitude ?? ""} onChange={(e) => setEditing({ ...editing, longitude: e.target.value === "" ? null : parseFloat(e.target.value) })} />
                </div>
              </div>
              <div><Label className="mb-1.5 block">Image</Label>
                <MediaUpload testid="rec-image" value={editing.image_url} onUploaded={(d) => setEditing({ ...editing, image_url: d.url })} onClear={() => setEditing({ ...editing, image_url: "" })} />
              </div>
              <div><Label className="mb-1.5 block">Tags</Label>
                <div className="flex gap-2">
                  <Input data-testid="rec-tag-input" value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())} placeholder="Add tag + Enter" />
                  <Button type="button" variant="outline" onClick={addTag} className="rounded-full">Add</Button>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {editing.tags.map((t, i) => (
                    <span key={i} className="flex items-center gap-1 text-xs bg-brand-sand text-brand-forest rounded-full px-2.5 py-1">
                      {t}<button onClick={() => setEditing({ ...editing, tags: editing.tags.filter((_, x) => x !== i) })}><X size={12} /></button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={save} data-testid="rec-save" className="rounded-full bg-brand-forest hover:bg-brand-terracotta text-white"><Save size={16} className="mr-2" />Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
